import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import Layout from "../../components/layout/Layout";
import { useCompanies } from "../../api/queries/organizationQueries";
import { useEmployeeImportStatus } from "../../api/queries/employeeQueries";
import { useEffectiveMapping } from "../../api/queries/adminToolsQueries";
import { queryKeys } from "../../api/queries/queryKeys";
import { getApiErrorMessage, getApiErrorStatus } from "../../utils/apiError";
import ImportUploadForm from "./components/import/ImportUploadForm";
import ImportProgress from "./components/import/ImportProgress";
import ImportResults from "./components/import/ImportResults";
import { activeImportStorage } from "./components/import/importHelpers";
import ImportTabs from "../admin/data-tools/ImportTabs";
import "./components/import/EmployeeImport.css";

/**
 * Employee import (runs in the background on the server).
 *   /employee/import            → A. upload form
 *   /employee/import/:batchId   → B. live progress, then C. results
 * The batch id lives in the URL (and sessionStorage while running), so a
 * refresh or navigating away and back resumes the same view.
 */
const EmployeeImportPage = () => {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const userCompanyId = useSelector((s) => s.auth.companyId);
  const { data: companies = [] } = useCompanies();

  const effectiveMapping = useEffectiveMapping(userCompanyId || undefined, {
    enabled: !batchId,
  });
  const mapping = effectiveMapping.data;
  const mappingName = mapping
    ? !mapping.id || String(mapping.id).startsWith("default-")
      ? "Built-in default"
      : mapping.name
    : null;
  const statusQuery = useEmployeeImportStatus(batchId);
  const batch = statusQuery.data;
  const isFinished = !!batch && batch.status !== "PROCESSING";
  const notFound =
    statusQuery.isError && getApiErrorStatus(statusQuery.error) === 404;
  // Retrying after a network/5xx error while we already have progress on screen
  const isReconnecting =
    !!batch && statusQuery.failureCount > 0 && statusQuery.isFetching;

  // Offer to resume a running import when landing on the upload form
  const [resumable, setResumable] = useState(() =>
    batchId ? null : activeImportStorage.get(),
  );

  // Remember the running batch; forget it once it is finished or unknown
  useEffect(() => {
    if (!batchId) return;
    if (batch?.status === "PROCESSING") {
      activeImportStorage.set({ batchId, fileName: batch.fileName });
    } else if (
      (isFinished || notFound) &&
      activeImportStorage.get()?.batchId === batchId
    ) {
      activeImportStorage.clear();
    }
  }, [batchId, batch?.status, batch?.fileName, isFinished, notFound]);

  // When the import finishes, refresh lists so new employees show up (once per batch)
  const refreshedFor = useRef(null);
  useEffect(() => {
    if (isFinished && refreshedFor.current !== batchId) {
      refreshedFor.current = batchId;
      queryClient.invalidateQueries({ queryKey: queryKeys.employees.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.importLogs.all });
    }
  }, [isFinished, batchId, queryClient]);

  const handleStarted = ({ batchId: newBatchId, totalRows, fileName }) => {
    // Show the progress view immediately; the first poll fills in the rest
    queryClient.setQueryData(queryKeys.employeeImport.batch(newBatchId), {
      status: "PROCESSING",
      batchId: newBatchId,
      fileName,
      stats: {
        total: totalRows || 0,
        processed: 0,
        success: 0,
        partial: 0,
        failed: 0,
      },
      results: [],
    });
    activeImportStorage.set({ batchId: newBatchId, fileName });
    navigate(`/employee/import/${newBatchId}`);
  };

  const handleImportAnother = () => {
    activeImportStorage.clear();
    setResumable(null);
    navigate("/employee/import");
  };

  return (
    <Layout>
      <div className="container-fluid py-4 px-lg-4 employee-import-page">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
          <div>
            <h1 className="fw-bold text-dark m-0 page-title">Import</h1>
            <p className="text-muted small mb-0">
              Add many new employees at once, with all their details, from one
              Excel workbook.
            </p>
            {!batchId && mappingName && (
              <p className="fs-080 text-slate-500 mb-0 mt-1">
                <i className="bi bi-diagram-3 me-1"></i>Sheet and column names
                come from: <strong>{mappingName}</strong> ·{" "}
                <Link to="/admin/data-mapping">Workbook columns</Link>
              </p>
            )}
          </div>
          <button
            type="button"
            className="btn btn-sm px-3 fw-semibold d-flex align-items-center profile-btn-white h-35px rounded-8"
            onClick={() => navigate("/employee")}
          >
            <i className="bi bi-arrow-left me-2"></i> Back to employees
          </button>
        </div>

        <ImportTabs />

        {/* A. Upload */}
        {!batchId && (
          <>
            {resumable?.batchId && (
              <div
                className="alert alert-info d-flex flex-wrap align-items-center justify-content-between gap-2"
                role="status"
              >
                <span>
                  <i className="bi bi-hourglass-split me-2"></i>
                  An import
                  {resumable.fileName ? ` of ${resumable.fileName}` : ""} may
                  still be running.
                </span>
                <span className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-sm btn-primary"
                    onClick={() =>
                      navigate(`/employee/import/${resumable.batchId}`)
                    }
                  >
                    View progress
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={handleImportAnother}
                  >
                    Dismiss
                  </button>
                </span>
              </div>
            )}
            <ImportUploadForm
              companies={companies}
              defaultCompanyId={userCompanyId}
              onStarted={handleStarted}
            />
          </>
        )}

        {/* Loading an existing batch (e.g. after a refresh) */}
        {batchId && !batch && statusQuery.isPending && (
          <div className="import-card text-center py-5">
            <span
              className="spinner-border text-primary"
              role="status"
              aria-label="Loading import"
            ></span>
          </div>
        )}

        {/* Unknown batch / could not load */}
        {batchId && !batch && statusQuery.isError && (
          <div className="import-card">
            <div className="alert alert-danger mb-3" role="alert">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              {notFound
                ? "This import batch was not found. It may have expired or the link is wrong."
                : getApiErrorMessage(
                    statusQuery.error,
                    "Could not load the import progress.",
                  )}
            </div>
            <div className="d-flex gap-2">
              {!notFound && (
                <button
                  type="button"
                  className="btn btn-outline-secondary profile-btn"
                  onClick={() => statusQuery.refetch()}
                >
                  <i className="bi bi-arrow-clockwise"></i> Try again
                </button>
              )}
              <button
                type="button"
                className="btn btn-dark profile-btn"
                onClick={handleImportAnother}
              >
                <i className="bi bi-upload"></i> Start a new import
              </button>
            </div>
          </div>
        )}

        {/* B. Processing */}
        {batch && !isFinished && (
          <ImportProgress batch={batch} isReconnecting={isReconnecting} />
        )}

        {/* C. Finished (key resets filters if another batch is opened) */}
        {batch && isFinished && (
          <ImportResults
            key={batch.batchId}
            batch={batch}
            onImportAnother={handleImportAnother}
          />
        )}
      </div>
    </Layout>
  );
};

export default EmployeeImportPage;
